import { lazy, Suspense, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import { VideoPlayerLayout, VideoPlayerLayoutSkeleton } from "@/components/VideoPlayerLayout";
import {
  useSharedPlaylistParam,
  useSharedPlaylistCourseParam,
} from "@/hooks/useUrlParams";
import { useCourseVideoDirect, useCourseVideoIndex } from "@/hooks/useVideos";
import { useCourseItem } from "@/hooks/useCourses";
import { useAuth } from "@/lib/auth";
import * as api from "@/lib/api";

const SharedPlaylistComments = lazy(() =>
  import("@/components/comments/SharedPlaylistComments").then((m) => ({
    default: m.SharedPlaylistComments,
  })),
);

export function SharedPlaylistCoursePage() {
  const playlistToken = useSharedPlaylistParam();
  const courseId = useSharedPlaylistCourseParam();
  const { user, loading: authLoading } = useAuth();
  const trackedPlaylistTokenRef = useRef<string | null>(null);

  // Guest share tracking (same as /app/shared-playlist). Users often open course-shared first.
  useEffect(() => {
    if (
      !playlistToken ||
      !courseId ||
      trackedPlaylistTokenRef.current === playlistToken ||
      authLoading ||
      !!user
    ) {
      return;
    }

    trackedPlaylistTokenRef.current = playlistToken;
    void api.trackShareOpen("playlist", playlistToken);
  }, [authLoading, user, playlistToken, courseId]);

  // Priority: fetch only this course's videos (not all videos)
  const { data: courseVideos, loading: videosLoading } = useCourseVideoDirect(courseId);
  const { data: videoIndex } = useCourseVideoIndex(courseId);

  // Non-blocking: course details for title/instructor (not needed for video playback)
  const { data: course } = useCourseItem(courseId);

  const courseName = course?.name ?? "Shared Course";
  const instructorName = (course as any)?.Instructor?.name ?? "Khusbu Gemmati";
  const backUrl = playlistToken
    ? `/app/shared-playlist?shared_playlist=${playlistToken}`
    : "/";

  if (!courseId) {
    return (
      <div className="flex min-h-screen flex-col bg-[#f6f5f6] text-[#1c171b]">
        <SharedTopbar title="Invalid Link" backUrl={backUrl} />
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="text-center">
            <p className="text-[0.96rem] text-[#70696f]">No course was specified.</p>
            <Link
              to="/"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-[0.9rem] bg-[#762a5c] px-6 py-3 text-[0.93rem] font-semibold text-white"
            >
              Go to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#f6f5f6] text-[#1c171b]">
      <SharedTopbar title={courseName} backUrl={backUrl} />
      <main className="flex flex-1 flex-col">
        {videosLoading ? (
          <VideoPlayerLayoutSkeleton />
        ) : (
          <VideoPlayerLayout
            courseVideos={courseVideos ?? []}
            videoIndex={videoIndex}
            instructorName={instructorName}
          />
        )}
        <Suspense fallback={null}>
          <SharedPlaylistComments playlistToken={playlistToken} />
        </Suspense>
      </main>
    </div>
  );
}

function SharedTopbar({ title, backUrl }: { title: string; backUrl: string }) {
  return (
    <header className="flex min-h-[4.5rem] items-center gap-5 border-b border-[#e2dce0] bg-white px-[clamp(1rem,3vw,3rem)] max-[767px]:min-h-[4rem]">
      <a
        href="https://bollyrhythm.com"
        className="flex shrink-0 items-center"
      >
        <img
          src="/logo-dark.png"
          alt="BollyRhythm"
          className="h-7 w-auto"
        />
      </a>
      <div className="h-5 w-px bg-[#e2dce0]" />
      <h1 className="m-0 truncate font-[family-name:var(--font-display)] text-[1.15rem] font-medium leading-[1.2] text-[#321b29]">
        {title}
      </h1>
      <Link
        to={backUrl}
        className="ml-auto inline-flex items-center gap-2 text-[0.875rem] font-medium text-[#525866] transition-colors hover:text-[#321b29]"
      >
        <span className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-full border border-[#e2e4e9] bg-white">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className="h-4 w-4">
            <path d="M224,128a8,8,0,0,1-8,8H59.31l58.35,58.34a8,8,0,0,1-11.32,11.32l-72-72a8,8,0,0,1,0-11.32l72-72a8,8,0,0,1,11.32,11.32L59.31,120H216A8,8,0,0,1,224,128Z" />
          </svg>
        </span>
        Back to Performance
      </Link>
    </header>
  );
}
